import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { apiRequest } from "@/lib/queryClient";

export type Language =
  | "en" | "hi" | "ta" | "te" | "bn" | "mr" | "kn" | "ml" | "gu" | "pa"
  | "or" | "as" | "ur" | "sa" | "kok" | "mni" | "brx" | "doi" | "mai"
  | "sat" | "sd" | "ks" | "ne" | "bho"
  | "es" | "pt" | "fr" | "de" | "it" | "ru" | "zh" | "ja" | "ko" | "ar"
  | "tr" | "id" | "vi" | "th"
  | "fa" | "sw" | "fil" | "ms" | "pl" | "uk" | "nl" | "ro" | "el" | "cs"
  | "hu" | "sv" | "he" | "am" | "ha" | "yo" | "my" | "km";

interface LanguageInfo {
  code: Language;
  name: string;
  nativeName: string;
}

export const LANGUAGES: LanguageInfo[] = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी" },
  { code: "ta", name: "Tamil", nativeName: "தமிழ்" },
  { code: "te", name: "Telugu", nativeName: "తెలుగు" },
  { code: "bn", name: "Bengali", nativeName: "বাংলা" },
  { code: "mr", name: "Marathi", nativeName: "मराठी" },
  { code: "kn", name: "Kannada", nativeName: "ಕನ್ನಡ" },
  { code: "ml", name: "Malayalam", nativeName: "മലയാളം" },
  { code: "gu", name: "Gujarati", nativeName: "ગુજરાતી" },
  { code: "pa", name: "Punjabi", nativeName: "ਪੰਜਾਬੀ" },
  { code: "or", name: "Odia", nativeName: "ଓଡ଼ିଆ" },
  { code: "as", name: "Assamese", nativeName: "অসমীয়া" },
  { code: "ur", name: "Urdu", nativeName: "اردو" },
  { code: "sa", name: "Sanskrit", nativeName: "संस्कृतम्" },
  { code: "kok", name: "Konkani", nativeName: "कोंकणी" },
  { code: "mni", name: "Manipuri", nativeName: "মৈতৈলোন্" },
  { code: "brx", name: "Bodo", nativeName: "बड़ो" },
  { code: "doi", name: "Dogri", nativeName: "डोगरी" },
  { code: "mai", name: "Maithili", nativeName: "मैथिली" },
  { code: "sat", name: "Santali", nativeName: "ᱥᱟᱱᱛᱟᱲᱤ" },
  { code: "sd", name: "Sindhi", nativeName: "سنڌي" },
  { code: "ks", name: "Kashmiri", nativeName: "कॉशुर" },
  { code: "ne", name: "Nepali", nativeName: "नेपाली" },
  { code: "bho", name: "Bhojpuri", nativeName: "भोजपुरी" },
  { code: "es", name: "Spanish", nativeName: "Español" },
  { code: "pt", name: "Portuguese", nativeName: "Português" },
  { code: "fr", name: "French", nativeName: "Français" },
  { code: "de", name: "German", nativeName: "Deutsch" },
  { code: "it", name: "Italian", nativeName: "Italiano" },
  { code: "ru", name: "Russian", nativeName: "Русский" },
  { code: "zh", name: "Chinese (Simplified)", nativeName: "中文" },
  { code: "ja", name: "Japanese", nativeName: "日本語" },
  { code: "ko", name: "Korean", nativeName: "한국어" },
  { code: "ar", name: "Arabic", nativeName: "العربية" },
  { code: "tr", name: "Turkish", nativeName: "Türkçe" },
  { code: "id", name: "Indonesian", nativeName: "Bahasa Indonesia" },
  { code: "vi", name: "Vietnamese", nativeName: "Tiếng Việt" },
  { code: "th", name: "Thai", nativeName: "ไทย" },
  { code: "fa", name: "Persian", nativeName: "فارسی" },
  { code: "sw", name: "Swahili", nativeName: "Kiswahili" },
  { code: "fil", name: "Filipino", nativeName: "Filipino" },
  { code: "ms", name: "Malay", nativeName: "Bahasa Melayu" },
  { code: "pl", name: "Polish", nativeName: "Polski" },
  { code: "uk", name: "Ukrainian", nativeName: "Українська" },
  { code: "nl", name: "Dutch", nativeName: "Nederlands" },
  { code: "ro", name: "Romanian", nativeName: "Română" },
  { code: "el", name: "Greek", nativeName: "Ελληνικά" },
  { code: "cs", name: "Czech", nativeName: "Čeština" },
  { code: "hu", name: "Hungarian", nativeName: "Magyar" },
  { code: "sv", name: "Swedish", nativeName: "Svenska" },
  { code: "he", name: "Hebrew", nativeName: "עברית" },
  { code: "am", name: "Amharic", nativeName: "አማርኛ" },
  { code: "ha", name: "Hausa", nativeName: "Hausa" },
  { code: "yo", name: "Yoruba", nativeName: "Yorùbá" },
  { code: "my", name: "Burmese", nativeName: "မြန်မာ" },
  { code: "km", name: "Khmer", nativeName: "ខ្មែរ" },
];

const RTL_LANGUAGES = new Set<Language>(["ar", "fa", "he", "sd", "ur"]);

export function isRtlLanguage(language: Language): boolean {
  return RTL_LANGUAGES.has(language);
}

type TranslationDictionary = Record<string, Record<string, string>>;

let nonEnglishTranslationsPromise: Promise<TranslationDictionary> | null = null;

function loadNonEnglishTranslations(): Promise<TranslationDictionary> {
  nonEnglishTranslationsPromise ??= import("./translationsGlobal").then(
    ({ globalTranslations }) => globalTranslations,
  );
  return nonEnglishTranslationsPromise;
}

// English stays in the initial bundle so t() always has a synchronous fallback.
// Every non-English dictionary is loaded on demand from translationsGlobal.ts.
const translations: Partial<Record<Language, Record<string, string>>> = {
  en: {
    "app.name": "ZEASTRO",
    "app.tagline": "Know Yourself. Understand Others.",
    "nav.home": "Home",
    "nav.compatibility": "Compatibility",
    "nav.team": "Team Compatibility",
    "nav.pricing": "Pricing",
    "nav.birth_chart": "Birth Chart",
    "nav.matches": "Matches",
    "nav.logout": "Logout",
    "nav.login": "Sign In",
    "nav.signup": "Sign Up",
    "nav.dashboard": "Dashboard",
    "nav.forgot_password": "Forgot password?",
    "nav.find_matches": "Compatibility Assistant",
    "nav.kundali_match": "Kundali",
    "nav.family": "Family",
    "nav.family_group": "Family Group",
    "nav.classroom": "Classroom",
    "nav.daily_horoscope": "Daily Horoscope",
    "nav.tarot": "Tarot Reading",
    "nav.numerology": "Numerology",
    "nav.cosmic_science": "Cosmic Science",
    "nav.ai_coach": "AI Coach",
    "nav.relationship_coach": "Relationship Coach",
    "nav.wellness": "Wellness",
    "nav.cosmic_nutrition": "Cosmic Nutrition",
    "nav.journal": "Journal",
    "nav.challenges": "Challenges",
    "nav.daily_progress": "Daily Progress",
    "nav.daily_growth": "Daily Growth",
    "nav.improvement": "Improvement",
    "nav.ai_avatar": "AI Avatar",
    "nav.profile": "Profile",
    "nav.more": "More",
    "nav.explore_features": "Explore Features",
    "nav.match_short": "Match",
    "nav.tarot_short": "Tarot",
    "nav.coach_short": "Coach",
    "nav.section.compatibility": "Compatibility",
    "nav.section.insights": "Cosmic Insights",
    "nav.section.wellness": "Wellness & Growth",
    "nav.section.progress": "Progress",
    "nav.section.account": "Account",
    "common.user": "User",
    "common.free": "Free",
    "landing.hero_title": "Discover Your Cosmic Destiny",
    "landing.hero_subtitle": "AI-powered personalized astrological insights inspired by classic golden-age astrology",
    "landing.get_started": "Get Started",
    "landing.learn_more": "Learn More",
    "landing.features_title": "Why ZEASTRO?",
    "landing.feature_ai": "AI-Powered Readings",
    "landing.feature_ai_desc": "Get deeply personalized astrological insights powered by advanced AI",
    "landing.feature_compat": "Compatibility Analysis",
    "landing.feature_compat_desc": "Discover your cosmic connections with detailed compatibility scoring",
    "landing.feature_chart": "Birth Charts",
    "landing.feature_chart_desc": "Generate detailed natal charts with planetary positions and interpretations",
    "landing.subscribe_title": "Get Daily Horoscope Updates",
    "landing.subscribe_placeholder": "Enter your email",
    "landing.subscribe_button": "Subscribe",
    "landing.subscribed": "Subscribed!",
    "home.welcome": "Welcome",
    "home.your_sign": "Your Zodiac Sign",
    "home.daily_horoscope": "Today's Horoscope",
    "home.mood": "Mood",
    "home.lucky_number": "Lucky Number",
    "home.lucky_color": "Lucky Color",
    "home.keywords": "Keywords",
    "home.recent_reports": "Recent Reports",
    "home.no_reports": "No compatibility reports yet",
    "home.quick_actions": "Quick Actions",
    "compatibility.title": "Compatibility Analysis",
    "compatibility.select_signs": "Select two zodiac signs to analyze",
    "compatibility.sign1": "First Sign",
    "compatibility.sign2": "Second Sign",
    "compatibility.type": "Relationship Type",
    "compatibility.analyze": "Analyze Compatibility",
    "compatibility.results": "Results",
    "compatibility.overall": "Overall Score",
    "compatibility.emotional": "Emotional",
    "compatibility.communication": "Communication",
    "compatibility.trust": "Trust",
    "compatibility.values": "Values",
    "matches.title": "Your Compatibility Matches",
    "matches.subtitle": "Discover who you're most compatible with based on your zodiac sign",
    "matches.select_type": "Select Relationship Type",
    "matches.detailed": "Detailed Analysis",
    "matches.no_analysis": "No analysis available - click to generate",
    "matches.excellent": "Excellent Match",
    "matches.good": "Good Match",
    "matches.challenging": "Challenging Match",
    "team.title": "Team Compatibility Analysis",
    "team.description": "Analyze zodiac compatibility within your team",
    "team.add_member": "Add Team Member",
    "team.analyze": "Analyze Team",
    "pricing.title": "Choose Your Plan",
    "pricing.subtitle": "Unlock the full power of cosmic insights",
    "pricing.free": "Free",
    "pricing.cosmic": "Cosmic",
    "pricing.celestial": "Cosmic Pro",
    "pricing.enterprise": "Enterprise",
    "pricing.per_month": "/month",
    "pricing.current_plan": "Current Plan",
    "pricing.upgrade": "Upgrade",
    "pricing.popular": "Most Popular",
    "birth_chart.title": "Natal Birth Chart",
    "birth_chart.description": "Calculate your detailed birth chart with planetary positions",
    "birth_chart.calculate": "Calculate Chart",
    "birth_chart.date": "Birth Date",
    "birth_chart.time": "Birth Time",
    "birth_chart.location": "Birth Location",
    "auth.login_title": "Welcome Back",
    "auth.login_subtitle": "Sign in to your cosmic journey",
    "auth.signup_title": "Join the Cosmos",
    "auth.signup_subtitle": "Create your account and discover your destiny",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.confirm_password": "Confirm Password",
    "auth.login_button": "Sign In",
    "auth.signup_button": "Create Account",
    "auth.no_account": "Don't have an account?",
    "auth.has_account": "Already have an account?",
    "auth.forgot_password": "Forgot password?",
    "auth.reset_password": "Reset Password",
    "auth.new_password": "New Password",
    "auth.reset_token": "Reset Token",
    "auth.send_reset": "Send Reset Link",
    "auth.back_to_login": "Back to Login",
    "common.loading": "Loading...",
    "common.error": "Something went wrong",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.submit": "Submit",
    "common.back": "Back",
    "common.next": "Next",
    "common.share": "Share",
    "common.copy_link": "Copy Link",
    "common.theme": "Theme",
    "common.language": "Language",
    "common.light": "Light",
    "common.dark": "Dark",
    "zodiac.element": "Element",
    "zodiac.quality": "Quality",
    "zodiac.ruling_planet": "Ruling Planet",
    "zodiac.date_range": "Date Range",
    "zodiac.strengths": "Strengths",
    "zodiac.weaknesses": "Weaknesses",
    "zodiac.fire": "Fire",
    "zodiac.earth": "Earth",
    "zodiac.air": "Air",
    "zodiac.water": "Water",
    "upgrade.title": "Upgrade Required",
    "upgrade.description": "This feature requires a premium subscription",
    "upgrade.button": "View Plans",
  },

};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  isLanguageSwitching: boolean;
  languageSwitchId: number;
  completeLanguageSwitch: (id: number) => void;
  t: (key: string, defaultValue?: string) => string;
  languages: LanguageInfo[];
}

const LanguageContext = createContext<LanguageContextType>({
  language: "en",
  setLanguage: () => {},
  isLanguageSwitching: false,
  languageSwitchId: 0,
  completeLanguageSwitch: () => {},
  t: (key: string, defaultValue?: string) => defaultValue || key,
  languages: LANGUAGES,
});

// Persist the selected language to the user account so the choice follows
// them across devices and influences server-generated content (daily
// horoscope, AI assistant). Best-effort — anonymous visitors and offline
// errors are silently ignored; localStorage remains the source of truth
// for the current device.
async function persistLanguageToAccount(lang: Language): Promise<void> {
  try {
    await apiRequest("PATCH", "/api/user/profile", { preferredLanguage: lang });
  } catch {
    // ignore
  }
}

const KNOWN_LANGUAGES = new Set<Language>(LANGUAGES.map((l) => l.code));
function isLanguage(value: unknown): value is Language {
  return typeof value === "string" && KNOWN_LANGUAGES.has(value as Language);
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  // The login page must not bootstrap the session just to discover a language.
  // Once authentication is established, the shared auth query below provides
  // the user's persisted language without issuing a second user request.
  const { user } = useAuth();
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("cosmic-language");
      if (isLanguage(stored)) return stored;
    }
    return "en";
  });
  const [nonEnglishTranslations, setNonEnglishTranslations] =
    useState<TranslationDictionary>({});
  const [languageSwitch, setLanguageSwitch] = useState({ active: false, id: 0 });

  useEffect(() => {
    if (language === "en" || nonEnglishTranslations[language]) return;

    let cancelled = false;
    void loadNonEnglishTranslations().then((loadedTranslations) => {
      if (!cancelled) setNonEnglishTranslations(loadedTranslations);
    });

    return () => {
      cancelled = true;
    };
  }, [language, nonEnglishTranslations]);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = isRtlLanguage(language) ? "rtl" : "ltr";
  }, [language]);

  const completeLanguageSwitch = useCallback((id: number) => {
    setLanguageSwitch((current) =>
      current.active && current.id === id ? { ...current, active: false } : current,
    );
  }, []);

  useEffect(() => {
    if (!languageSwitch.active) return;
    const timer = window.setTimeout(
      () => completeLanguageSwitch(languageSwitch.id),
      8_000,
    );
    return () => window.clearTimeout(timer);
  }, [languageSwitch, completeLanguageSwitch]);

  // The shared auth query supplies the user's persisted language on first load
  // and after sign-in. The local choice still wins — we only override if the
  // device has no preference yet.
  useEffect(() => {
    const remote: unknown = user?.preferredLanguage;
    if (!isLanguage(remote)) return;
    const localStored = localStorage.getItem("cosmic-language");
    if (!isLanguage(localStored)) {
      setLanguageState(remote);
      localStorage.setItem("cosmic-language", remote);
    }
  }, [user]);

  const queryClient = useQueryClient();

  // When the language changes, refetch only the server queries whose
  // responses are actually localized (they read the X-Language header).
  // Invalidating *everything* here used to trigger a refetch storm on
  // language switch — dozens of unrelated queries re-rendering the page
  // while the DOM translator was re-scanning it, which made switching
  // feel sluggish.
  useEffect(() => {
    const LOCALIZED_PREFIXES = [
      "/api/horoscope/",
      "/api/wellness/",
      "/api/jyotish/festivals",
      "/api/tarot/card-of-day",
      "/api/content",
    ];
    void queryClient.invalidateQueries({
      predicate: (query) => {
        const first = query.queryKey[0];
        return (
          typeof first === "string" &&
          LOCALIZED_PREFIXES.some((p) => first.startsWith(p))
        );
      },
    });
  }, [language, queryClient]);

  const setLanguage = (lang: Language) => {
    if (lang === language || languageSwitch.active) return;
    setLanguageSwitch((current) => ({ active: true, id: current.id + 1 }));
    setLanguageState(lang);
    localStorage.setItem("cosmic-language", lang);
    void persistLanguageToAccount(lang);
  };

  const t = (key: string, defaultValue?: string): string => {
    return (
      nonEnglishTranslations[language]?.[key] ||
      translations.en?.[key] ||
      defaultValue ||
      key
    );
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        t,
        languages: LANGUAGES,
        isLanguageSwitching: languageSwitch.active,
        languageSwitchId: languageSwitch.id,
        completeLanguageSwitch,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
